import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Bell, Inbox, FileSignature, Receipt, X } from "lucide-react";
import { API_BASE } from "./AuthContext";

/**
 * v68.18 — Portal notifications bell.
 *
 * Compact bell button rendered in the client portal sidebar. Polls
 * `/api/portal/notifications/summary` on mount + every 45s. Shows a numeric
 * badge with the `unseen_count` (items created after `portal_last_seen_at`).
 * Opening the popover marks everything seen via
 * `POST /api/portal/notifications/mark-seen` so the badge clears.
 */
const KIND_META = {
  file_request: { label: "New file request", icon: Inbox,        to: "/portal/files" },
  esign:        { label: "Signature request", icon: FileSignature, to: "/portal/signatures" },
  invoice:      { label: "Invoice",             icon: Receipt,       to: "/portal/invoices" },
};

export default function PortalNotificationsBell() {
  const [summary, setSummary] = useState({ counts: { total_actionable: 0 }, items: [], unseen_count: 0 });
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const popRef = useRef(null);
  const btnRef = useRef(null);

  const reload = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_BASE}/portal/notifications/summary`);
      setSummary(data);
    } catch { /* ignore — portal may not be authenticated during a magic link consumption */ }
  }, []);

  useEffect(() => {
    reload();
    const id = setInterval(reload, 45_000);
    return () => clearInterval(id);
  }, [reload]);

  // Click outside → close
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (popRef.current?.contains(e.target)) return;
      if (btnRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const onToggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && summary.unseen_count > 0) {
      try {
        await axios.post(`${API_BASE}/portal/notifications/mark-seen`);
        // Optimistically clear the badge; we don't need to refetch immediately.
        setSummary((s) => ({ ...s, unseen_count: 0, items: (s.items || []).map((it) => ({ ...it, unseen: false })) }));
      } catch { /* swallow */ }
    }
  };

  const badge = summary.unseen_count || 0;
  const total = summary.counts?.total_actionable || 0;

  return (
    <div className="relative">
      <button
        ref={btnRef}
        onClick={onToggle}
        data-testid="portal-notifications-bell"
        className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-[13px] transition ${
          open ? "bg-[var(--subtle)] text-[var(--ink)]" : "text-[var(--ink-2)] hover:text-[var(--ink)] hover:bg-[var(--subtle)]"
        }`}
        aria-label="Notifications"
      >
        <Bell size={14} />
        <span className="flex-1 text-left">Notifications</span>
        {badge > 0 && (
          <span
            data-testid="portal-notifications-badge"
            className="text-[10px] font-mono rounded-full px-1.5 py-[1px] leading-none"
            style={{ background: "var(--accent)", color: "var(--paper)" }}
          >
            {badge}
          </span>
        )}
      </button>

      {open && (
        <div
          ref={popRef}
          data-testid="portal-notifications-popover"
          className="absolute z-50 left-full ml-2 top-0 w-80 rounded-lg shadow-2xl overflow-hidden border border-[var(--border)]"
          style={{ background: "var(--paper)" }}
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--border)]">
            <div>
              <div className="overline text-[9px]">Activity</div>
              <div className="text-[12px] text-[var(--ink-2)]">
                {total === 0 ? "You're all caught up" :
                 `${total} item${total === 1 ? "" : "s"} need${total === 1 ? "s" : ""} attention`}
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="text-[var(--ink-3)] hover:text-[var(--ink)]"
                    aria-label="Close" data-testid="portal-notifications-close">
              <X size={14} />
            </button>
          </div>

          {/* Section summary tiles */}
          <div className="grid grid-cols-3 gap-1 p-2 border-b border-[var(--border)] text-center">
            <SummaryTile label="Files" count={summary.counts?.file_requests || 0}
                         onClick={() => { setOpen(false); navigate("/portal/files"); }} testid="notif-tile-files" />
            <SummaryTile label="Signatures" count={summary.counts?.esigns || 0}
                         onClick={() => { setOpen(false); navigate("/portal/signatures"); }} testid="notif-tile-esigns" />
            <SummaryTile label="Invoices" count={summary.counts?.invoices || 0}
                         onClick={() => { setOpen(false); navigate("/portal/invoices"); }} testid="notif-tile-invoices" />
          </div>

          {/* Recent activity list */}
          <div className="max-h-72 overflow-y-auto">
            {(summary.items || []).length === 0 ? (
              <div className="px-4 py-8 text-center text-[12px] text-[var(--ink-3)]">
                Nothing recent
              </div>
            ) : (
              <ul className="divide-y divide-[var(--border)]">
                {summary.items.map((it, idx) => (
                  <NotifRow key={`${it.kind}-${it.id || idx}`} it={it}
                            onClick={() => { setOpen(false); navigate(KIND_META[it.kind]?.to || "/portal"); }} />
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryTile({ label, count, onClick, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
            className="rounded p-2 hover:bg-[var(--subtle)] transition">
      <div className={`font-heading text-lg leading-none ${count > 0 ? "text-[var(--accent)]" : "text-[var(--ink-3)]"}`}
           style={{ fontWeight: 500 }}>{count}</div>
      <div className="text-[10px] uppercase tracking-wider text-[var(--ink-3)] mt-1">{label}</div>
    </button>
  );
}

function NotifRow({ it, onClick }) {
  const meta = KIND_META[it.kind] || {};
  const Icon = meta.icon || Bell;
  const when = it.created_at ? new Date(it.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "";
  return (
    <li>
      <button onClick={onClick} data-testid={`portal-notif-row-${it.id}`}
              className="w-full text-left px-4 py-3 hover:bg-[var(--subtle)] transition flex items-start gap-3">
        <span className={`shrink-0 mt-0.5 ${it.unseen ? "text-[var(--accent)]" : "text-[var(--ink-3)]"}`}>
          <Icon size={13} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[11.5px] flex items-center gap-1.5">
            <span className="overline text-[9px]">{meta.label || it.kind}</span>
            {when && <span className="text-[var(--ink-3)]">· {when}</span>}
            {it.unseen && <span className="ml-auto w-1.5 h-1.5 rounded-full bg-[var(--accent)]" aria-label="unseen" />}
          </div>
          <div className="text-[13px] leading-tight text-[var(--ink)] truncate mt-0.5">
            {it.title}
          </div>
        </div>
      </button>
    </li>
  );
}
